import * as THREE from 'three';
import { MorseCodeUtils } from '../utils/MorseCodeUtils.js';

export class Pod extends THREE.Group {
    constructor() {
        super();

        // Create materials with glow effect
        this.bodyMaterial = new THREE.MeshPhongMaterial({
            color: 0x303030,
            emissive: 0x111111,
            shininess: 30,
            transparent: true,
            opacity: 0 // Start invisible
        });

        this.eyeMaterial = new THREE.MeshBasicMaterial({
            color: 0x4a90e2,
            transparent: true,
            opacity: 0 // Start invisible
        });
        
        // Store original eye color for reset
        this.originalEyeColor = 0x4a90e2;
        
        // Morse code properties
        this.useMorseCode = false; // Toggle for Morse code blinking
        this.morseMessage = "there is no God but Allah, and Muhammad is his final messenger";
        this.morseSequence = [];
        this.currentSequenceIndex = 0;
        this.isBlinking = false;
        this.blinkTimeout = null;
        this.blinkInterval = null;
        
        // Toolset view properties
        this.verticalDivider = null;
        this.leftLabel = null;
        this.rightLabel = null;
        this.rightEyePulsating = false;
        this.pulsateAnimationId = null;
        
        this.createPod();
        this.initMorseCode();
        this.startBlinking();
    }

    createPod() {
        // Body - perfect sphere
        const body = new THREE.Mesh(
            new THREE.SphereGeometry(1, 32, 32),
            this.bodyMaterial
        );

        // Create layered eyes
        this.leftEyeGroup = new THREE.Group();
        this.rightEyeGroup = new THREE.Group();

        // Create materials for each layer
        const outerMaterial = new THREE.MeshBasicMaterial({
            color: 0x1a1a1a,  // Very dark blue
            transparent: true,
            opacity: 0 // Start invisible
        });

        const middleMaterial = new THREE.MeshBasicMaterial({
            color: 0x2a4a7f,  // Darker blue
            transparent: true,
            opacity: 0 // Start invisible
        });

        // Create geometries for each layer
        const outerGeo = new THREE.CircleGeometry(0.22, 32);
        const middleGeo = new THREE.CircleGeometry(0.19, 32);
        const innerGeo = new THREE.CircleGeometry(0.15, 32);

        // Create left eye layers
        const leftOuter = new THREE.Mesh(outerGeo, outerMaterial);
        const leftMiddle = new THREE.Mesh(middleGeo, middleMaterial);
        const leftInner = new THREE.Mesh(innerGeo, this.eyeMaterial);

        // Position layers back to front
        leftOuter.position.z = 1;
        leftMiddle.position.z = 1;
        leftInner.position.z = 1;

        // Add to left eye group
        this.leftEyeGroup.add(leftOuter, leftMiddle, leftInner);

        // Create right eye layers (clone from left)
        const rightOuter = leftOuter.clone();
        const rightMiddle = leftMiddle.clone();
        const rightInner = leftInner.clone();
        this.rightEyeGroup.add(rightOuter, rightMiddle, rightInner);

        // Position eye groups
        this.leftEyeGroup.position.set(-0.3, 0, 0);
        this.rightEyeGroup.position.set(0.3, 0, 0);

        // Make eye groups always face camera
        this.leftEyeGroup.onBeforeRender = (renderer, scene, camera) => {
            this.leftEyeGroup.quaternion.copy(camera.quaternion);
        };
        this.rightEyeGroup.onBeforeRender = (renderer, scene, camera) => {
            this.rightEyeGroup.quaternion.copy(camera.quaternion);
        };

        // Store references for blinking
        this.leftEye = this.leftEyeGroup;
        this.rightEye = this.rightEyeGroup;

        this.add(body, this.leftEye, this.rightEye);
    }

    initMorseCode() {
        // Generate the Morse code sequence for our message
        this.morseSequence = MorseCodeUtils.generateBlinkSequence(this.morseMessage);
        console.log('Morse sequence generated:', this.morseSequence);
    }
    
    startBlinking() {
        if (this.useMorseCode) {
            // Start the Morse code sequence
            this.processNextMorseElement();
        } else {
            // Use random blinking as before
            this.blinkInterval = setInterval(() => {
                if (Math.random() < 0.5) { // 50% chance to blink
                    this.blink();
                }
            }, 1500); // Check every 1.5 seconds
        }
    }
    
    processNextMorseElement() {
        // If we've reached the end of the sequence, start over
        if (this.currentSequenceIndex >= this.morseSequence.length) {
            this.currentSequenceIndex = 0;
            // Add a much longer pause between repetitions (8-12 seconds)
            const pauseDuration = 8000 + Math.random() * 4000;
            
            // Add a few natural random blinks during the pause
            const randomBlinks = Math.floor(Math.random() * 3) + 1;
            for (let i = 0; i < randomBlinks; i++) {
                const blinkTime = Math.random() * pauseDuration;
                setTimeout(() => this.blink(), blinkTime);
            }
            
            setTimeout(() => this.processNextMorseElement(), pauseDuration);
            return;
        }
        
        const element = this.morseSequence[this.currentSequenceIndex];
        
        if (element.type === 'blink') {
            // Perform a blink with the specified duration
            this.morseBlink(element.duration);
        } else if (element.type === 'pause') {
            // Just wait for the specified duration
            setTimeout(() => {
                this.currentSequenceIndex++;
                this.processNextMorseElement();
            }, element.duration);
        }
    }
    
    morseBlink(duration) {
        if (this.isBlinking) return;
        
        this.isBlinking = true;
        
        // Close eyes (scale y to near zero)
        this.leftEye.children.forEach(layer => layer.scale.y = 0.01);
        this.rightEye.children.forEach(layer => layer.scale.y = 0.01);
        
        // Open eyes after the specified duration
        this.blinkTimeout = setTimeout(() => {
            this.leftEye.children.forEach(layer => layer.scale.y = 1);
            this.rightEye.children.forEach(layer => layer.scale.y = 1);
            
            this.isBlinking = false;
            this.currentSequenceIndex++;
            
            // Process the next element after a short delay
            setTimeout(() => this.processNextMorseElement(), 50);
        }, duration);
    }

    blink() {
        // Scale all children of the eye groups
        this.leftEye.children.forEach(layer => layer.scale.y = 0.01);
        this.rightEye.children.forEach(layer => layer.scale.y = 0.01);
        
        setTimeout(() => {
            this.leftEye.children.forEach(layer => layer.scale.y = 1);
            this.rightEye.children.forEach(layer => layer.scale.y = 1);
        }, 150);
    }

    update() {
        // Subtle floating motion
        this.position.y = Math.sin(Date.now() * 0.002) * 0.05;
    }
    
    enableMorseCode() {
        if (!this.useMorseCode) {
            console.log('Enabling Morse code blinking');
            this.useMorseCode = true;
            
            // Clear any existing blink intervals
            if (this.blinkInterval) {
                clearInterval(this.blinkInterval);
                this.blinkInterval = null;
            }
            
            // Start the Morse code sequence
            this.processNextMorseElement();
        }
    }
    
    disableMorseCode() {
        console.log('Disabling Morse code blinking');
        this.useMorseCode = false;
        
        // Clear ALL active timeouts that might be related to Morse code
        if (this.blinkTimeout) {
            clearTimeout(this.blinkTimeout);
            this.blinkTimeout = null;
        }
        
        // Clear any existing blink intervals
        if (this.blinkInterval) {
            clearInterval(this.blinkInterval);
            this.blinkInterval = null;
        }
        
        // Clear any other pending timeouts that might be in the queue
        // This is a more aggressive approach to ensure all timeouts are cleared
        const highestTimeoutId = setTimeout(() => {}, 0);
        for (let i = 0; i < highestTimeoutId; i++) {
            clearTimeout(i);
        }
        
        // Reset sequence index and state
        this.currentSequenceIndex = 0;
        this.isBlinking = false;
        
        // Make sure eyes are fully open
        if (this.leftEye && this.rightEye) {
            this.leftEye.children.forEach(layer => layer.scale.y = 1);
            this.rightEye.children.forEach(layer => layer.scale.y = 1);
        }
        
        // Restore random blinking with a new interval
        this.blinkInterval = setInterval(() => {
            if (Math.random() < 0.5) { // 50% chance to blink
                this.blink();
            }
        }, 1500);
    }
    
    stopBlinking() {
        console.log('Stopping all blinking');
        
        // Clear all blinking timeouts and intervals
        if (this.blinkTimeout) {
            clearTimeout(this.blinkTimeout);
            this.blinkTimeout = null;
        }
        
        if (this.blinkInterval) {
            clearInterval(this.blinkInterval);
            this.blinkInterval = null;
        }
        
        // Clear any other pending timeouts that might be in the queue
        const highestTimeoutId = setTimeout(() => {}, 0);
        for (let i = 0; i < highestTimeoutId; i++) {
            clearTimeout(i);
        }
        
        // Reset state
        this.useMorseCode = false;
        this.currentSequenceIndex = 0;
        this.isBlinking = false;
        
        // Make sure eyes are fully open
        if (this.leftEye && this.rightEye) {
            this.leftEye.children.forEach(layer => layer.scale.y = 1);
            this.rightEye.children.forEach(layer => layer.scale.y = 1);
        }
    }
    
    fadeIn(duration = 1500) {
        // Store all materials to animate
        const materials = [
            this.bodyMaterial,
            this.eyeMaterial
        ];
        
        // Add all eye layer materials
        this.leftEye.children.forEach(layer => materials.push(layer.material));
        this.rightEye.children.forEach(layer => materials.push(layer.material));
        
        // Remove duplicates (some materials might be reused)
        const uniqueMaterials = [...new Set(materials)];
        
        // Animate opacity from 0 to target values
        const targetOpacities = {
            [this.bodyMaterial.uuid]: 1,
            [this.eyeMaterial.uuid]: 0.8
        };
        
        // Set target opacities for eye layers
        this.leftEye.children.forEach((layer, index) => {
            const opacity = index === 2 ? 0.8 : 0.9; // Inner eye vs outer layers
            targetOpacities[layer.material.uuid] = opacity;
        });
        
        // Animate each material
        uniqueMaterials.forEach(material => {
            const targetOpacity = targetOpacities[material.uuid] || 1;
            
            // Start animation
            const startTime = Date.now();
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Ease-in function
                const easeProgress = progress * progress;
                
                // Update opacity
                material.opacity = targetOpacity * easeProgress;
                
                // Continue animation if not complete
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };
            
            // Start animation loop
            animate();
        });
    }
    
    /**
     * Helper function to create a color variant
     * @param {number} baseColor - The base color to modify
     * @param {number} lightenFactor - Positive to lighten, negative to darken
     * @returns {number} The modified color as a hex value
     */
    createColorVariant(baseColor, lightenFactor) {
        const color = new THREE.Color(baseColor);
        // Lighten or darken the color
        if (lightenFactor > 0) {
            // Lighten
            color.r = Math.min(1, color.r + (1 - color.r) * lightenFactor);
            color.g = Math.min(1, color.g + (1 - color.g) * lightenFactor);
            color.b = Math.min(1, color.b + (1 - color.b) * lightenFactor);
        } else {
            // Darken
            const darkenFactor = -lightenFactor;
            color.r = Math.max(0, color.r * (1 - darkenFactor));
            color.g = Math.max(0, color.g * (1 - darkenFactor));
            color.b = Math.max(0, color.b * (1 - darkenFactor));
        }
        return color.getHex();
    }

    /**
     * Set the colors of the pod's eyes with gradient layers
     * @param {number} leftColor - Hex color for left eye
     * @param {number} rightColor - Hex color for right eye
     * @param {boolean} keepOuterDark - Whether to keep the outer circle dark (default: false)
     */
    setEyeColors(leftColor, rightColor, keepOuterDark = false) {
        console.log(`Setting eye colors - Left: ${leftColor.toString(16)}, Right: ${rightColor.toString(16)}`);
        
        // Dark color for outer layer if keepOuterDark is true
        const outerColor = 0x1a1a1a; // Very dark blue/black
        
        // Create gradient colors for each eye
        const leftColors = [
            keepOuterDark ? outerColor : this.createColorVariant(leftColor, -0.5),  // Outer - darkest or fixed dark
            this.createColorVariant(leftColor, -0.2),  // Middle - darker
            leftColor                                  // Inner - base color
        ];
        
        const rightColors = [
            keepOuterDark ? outerColor : this.createColorVariant(rightColor, -0.5),  // Outer - darkest or fixed dark
            this.createColorVariant(rightColor, -0.2),  // Middle - darker
            rightColor                                  // Inner - base color
        ];
        
        // Apply colors to each eye layer
        if (this.leftEye && this.leftEye.children && this.leftEye.children.length >= 3) {
            for (let i = 0; i < 3; i++) {
                const layer = this.leftEye.children[i];
                if (layer && layer.material) {
                    // Create new material with the gradient color
                    const newMaterial = new THREE.MeshBasicMaterial({
                        color: leftColors[i],
                        transparent: true,
                        opacity: 1
                    });
                    
                    // Store old material for disposal
                    const oldMaterial = layer.material;
                    
                    // Assign new material
                    layer.material = newMaterial;
                    
                    // Dispose old material if it's not the shared eye material
                    if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                        oldMaterial.dispose();
                    }
                }
            }
        }
        
        if (this.rightEye && this.rightEye.children && this.rightEye.children.length >= 3) {
            for (let i = 0; i < 3; i++) {
                const layer = this.rightEye.children[i];
                if (layer && layer.material) {
                    // Create new material with the gradient color
                    const newMaterial = new THREE.MeshBasicMaterial({
                        color: rightColors[i],
                        transparent: true,
                        opacity: 1
                    });
                    
                    // Store old material for disposal
                    const oldMaterial = layer.material;
                    
                    // Assign new material
                    layer.material = newMaterial;
                    
                    // Dispose old material if it's not the shared eye material
                    if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                        oldMaterial.dispose();
                    }
                }
            }
        }
    }
    
    /**
     * Reset eye colors to original blue
     */
    /**
     * Set eye colors specifically for the stories section (African violet with glow)
     */
    setEyeColorsForStories() {
        console.log('Setting eye colors for stories section (African violet)');  
        
        // African violet color: 0x9966CC
        const africanViolet = 0x9966CC;
        
        // Keep the outermost layer unchanged (dark color)
        const outerColor = 0x1a1a1a;  // Very dark blue/black
        
        // Set middle and inner layers to African violet variants
        const middleColor = this.createColorVariant(africanViolet, -0.2);  // Slightly darker African violet
        const innerColor = africanViolet;  // Pure African violet for inner layer
        
        // Apply colors to each eye layer with glow effect
        if (this.leftEye && this.leftEye.children && this.leftEye.children.length >= 3) {
            // Apply outer layer (unchanged)
            if (this.leftEye.children[0] && this.leftEye.children[0].material) {
                const newMaterial = new THREE.MeshBasicMaterial({
                    color: outerColor,
                    transparent: true,
                    opacity: 1
                });
                
                const oldMaterial = this.leftEye.children[0].material;
                this.leftEye.children[0].material = newMaterial;
                
                if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                    oldMaterial.dispose();
                }
            }
            
            // Apply middle layer (darker African violet)
            if (this.leftEye.children[1] && this.leftEye.children[1].material) {
                const newMaterial = new THREE.MeshPhongMaterial({
                    color: middleColor,
                    transparent: true,
                    opacity: 1,
                    emissive: africanViolet,
                    emissiveIntensity: 0.5,
                    shininess: 60
                });
                
                const oldMaterial = this.leftEye.children[1].material;
                this.leftEye.children[1].material = newMaterial;
                
                if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                    oldMaterial.dispose();
                }
            }
            
            // Apply inner layer (pure African violet with glow)
            if (this.leftEye.children[2] && this.leftEye.children[2].material) {
                const newMaterial = new THREE.MeshPhongMaterial({
                    color: innerColor,
                    transparent: true,
                    opacity: 1,
                    emissive: africanViolet,
                    emissiveIntensity: 0.8,
                    shininess: 80
                });
                
                const oldMaterial = this.leftEye.children[2].material;
                this.leftEye.children[2].material = newMaterial;
                
                if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                    oldMaterial.dispose();
                }
            }
        }
        
        // Do the same for right eye
        if (this.rightEye && this.rightEye.children && this.rightEye.children.length >= 3) {
            // Apply outer layer (unchanged)
            if (this.rightEye.children[0] && this.rightEye.children[0].material) {
                const newMaterial = new THREE.MeshBasicMaterial({
                    color: outerColor,
                    transparent: true,
                    opacity: 1
                });
                
                const oldMaterial = this.rightEye.children[0].material;
                this.rightEye.children[0].material = newMaterial;
                
                if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                    oldMaterial.dispose();
                }
            }
            
            // Apply middle layer (darker African violet)
            if (this.rightEye.children[1] && this.rightEye.children[1].material) {
                const newMaterial = new THREE.MeshPhongMaterial({
                    color: middleColor,
                    transparent: true,
                    opacity: 1,
                    emissive: africanViolet,
                    emissiveIntensity: 0.5,
                    shininess: 60
                });
                
                const oldMaterial = this.rightEye.children[1].material;
                this.rightEye.children[1].material = newMaterial;
                
                if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                    oldMaterial.dispose();
                }
            }
            
            // Apply inner layer (pure African violet with glow)
            if (this.rightEye.children[2] && this.rightEye.children[2].material) {
                const newMaterial = new THREE.MeshPhongMaterial({
                    color: innerColor,
                    transparent: true,
                    opacity: 1,
                    emissive: africanViolet,
                    emissiveIntensity: 0.8,
                    shininess: 80
                });
                
                const oldMaterial = this.rightEye.children[2].material;
                this.rightEye.children[2].material = newMaterial;
                
                if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                    oldMaterial.dispose();
                }
            }
        }
    }
    
    /**
     * Reset all eye layers to their original colors
     */
    resetEyeColors() {
        // Create original eye layer colors
        const outerColor = 0x1a1a1a;  // Very dark blue
        const middleColor = 0x2a4a7f;  // Darker blue
        const innerColor = this.originalEyeColor;  // Original eye color
        
        // Array of original colors for each layer
        const originalColors = [outerColor, middleColor, innerColor];
        
        // Reset left eye layers
        if (this.leftEye && this.leftEye.children) {
            for (let i = 0; i < Math.min(3, this.leftEye.children.length); i++) {
                const layer = this.leftEye.children[i];
                if (layer && layer.material) {
                    // Create new material with original color
                    const newMaterial = new THREE.MeshBasicMaterial({
                        color: originalColors[i],
                        transparent: true,
                        opacity: 1
                    });
                    
                    // Store old material for disposal
                    const oldMaterial = layer.material;
                    
                    // Assign new material
                    layer.material = newMaterial;
                    
                    // Dispose old material if it's not the shared eye material
                    if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                        oldMaterial.dispose();
                    }
                }
            }
        }
        
        // Reset right eye layers
        if (this.rightEye && this.rightEye.children) {
            for (let i = 0; i < Math.min(3, this.rightEye.children.length); i++) {
                const layer = this.rightEye.children[i];
                if (layer && layer.material) {
                    // Create new material with original color
                    const newMaterial = new THREE.MeshBasicMaterial({
                        color: originalColors[i],
                        transparent: true,
                        opacity: 1
                    });
                    
                    // Store old material for disposal
                    const oldMaterial = layer.material;
                    
                    // Assign new material
                    layer.material = newMaterial;
                    
                    // Dispose old material if it's not the shared eye material
                    if (oldMaterial && oldMaterial !== this.eyeMaterial) {
                        oldMaterial.dispose();
                    }
                }
            }
        }
    }
    
    /**
     * Enable pulsating effect for the right eye
     */
    enableRightEyePulsating() {
        if (this.rightEyePulsating) return;
        
        this.rightEyePulsating = true;
        
        // Get the inner layer of the right eye
        const rightInnerEye = this.rightEye.children[2];
        if (!rightInnerEye || !rightInnerEye.material) return;
        
        // Store the base color (gold)
        const baseColor = new THREE.Color(0xFFD700);
        const dimColor = new THREE.Color(0xB8860B); // Darker gold
        
        // Start the pulsating animation
        const pulsate = () => {
            if (!this.rightEyePulsating) return;
            
            // Calculate a value between 0 and 1 based on time
            const pulseValue = (Math.sin(Date.now() * 0.002) + 1) / 2;
            
            // Interpolate between the base and dim colors
            const currentColor = new THREE.Color().lerpColors(dimColor, baseColor, pulseValue);
            rightInnerEye.material.color.copy(currentColor);
            
            // Continue animation
            this.pulsateAnimationId = requestAnimationFrame(pulsate);
        };
        
        // Start the animation
        pulsate();
    }
    
    /**
     * Disable pulsating effect for the right eye
     */
    disableRightEyePulsating() {
        this.rightEyePulsating = false;
        
        // Cancel the animation frame
        if (this.pulsateAnimationId) {
            cancelAnimationFrame(this.pulsateAnimationId);
            this.pulsateAnimationId = null;
        }
    }
    
    /**
     * Add a pulsing energy line divider to the pod
     */
    addVerticalDivider() {
        // Create a group to hold the main line and particle effects
        this.verticalDivider = new THREE.Group();
        
        // Calculate points that follow the sphere's curvature
        const points = [];
        const segments = 30; // More segments for smoother curve
        const radius = 1; // Pod radius
        
        // Create points along a vertical arc that follows the sphere's surface
        for (let i = 0; i <= segments; i++) {
            // Calculate angle from -PI/2 to PI/2 (bottom to top of sphere)
            const angle = (Math.PI * i / segments) - Math.PI/2;
            
            // Calculate y position using sine
            const y = Math.sin(angle) * radius;
            
            // Calculate z offset to make the line appear slightly in front of the sphere
            // Use cosine to calculate the distance from the center to the surface at this y-coordinate
            const surfaceZ = Math.cos(Math.asin(y/radius)) * radius;
            const zOffset = 0.01; // Small offset to prevent z-fighting
            
            points.push(new THREE.Vector3(0, y, surfaceZ + zOffset));
        }
        
        // Create the main energy line
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        
        // Create a glowing material for the line
        const lineMaterial = new THREE.LineBasicMaterial({ 
            color: 0x88CCFF, // Light blue glow
            transparent: true,
            opacity: 0.7,
            linewidth: 2
        });
        
        const energyLine = new THREE.Line(lineGeometry, lineMaterial);
        this.verticalDivider.add(energyLine);
        
        // Add particle system for the energy effect
        const particleCount = 20;
        const particleGeometry = new THREE.BufferGeometry();
        
        // Create arrays to hold particle data
        const particlePositions = new Float32Array(particleCount * 3);
        const particleSizes = new Float32Array(particleCount);
        const particleOpacities = new Float32Array(particleCount);
        const particleSpeedFactors = new Float32Array(particleCount);
        
        // Initialize particles along the line
        for (let i = 0; i < particleCount; i++) {
            // Randomly position particles along the line
            const pointIndex = Math.floor(Math.random() * points.length);
            const point = points[pointIndex];
            
            // Set initial position
            particlePositions[i * 3] = point.x;
            particlePositions[i * 3 + 1] = point.y;
            particlePositions[i * 3 + 2] = point.z + 0.02; // Slightly in front of the line
            
            // Set random size
            particleSizes[i] = Math.random() * 0.04 + 0.01;
            
            // Set random opacity
            particleOpacities[i] = Math.random() * 0.7 + 0.3;
            
            // Set random speed factor
            particleSpeedFactors[i] = Math.random() * 0.8 + 0.2;
        }
        
        // Add attributes to the particle geometry
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
        particleGeometry.setAttribute('size', new THREE.BufferAttribute(particleSizes, 1));
        particleGeometry.setAttribute('opacity', new THREE.BufferAttribute(particleOpacities, 1));
        particleGeometry.setAttribute('speedFactor', new THREE.BufferAttribute(particleSpeedFactors, 1));
        
        // Create particle material
        const particleTexture = this.createParticleTexture();
        const particleMaterial = new THREE.PointsMaterial({
            size: 0.05,
            map: particleTexture,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            vertexColors: true
        });
        
        // Create the particle system
        const particles = new THREE.Points(particleGeometry, particleMaterial);
        this.verticalDivider.add(particles);
        
        // Store references to animate
        this.energyLine = energyLine;
        this.particles = particles;
        this.particlePositions = particlePositions;
        this.particleSizes = particleSizes;
        this.particleOpacities = particleOpacities;
        this.particleSpeedFactors = particleSpeedFactors;
        this.linePoints = points;
        
        // Start animation
        this.animateEnergyLine();
        
        // Add the divider to the pod
        this.add(this.verticalDivider);
    }
    
    /**
     * Create a circular particle texture
     */
    createParticleTexture() {
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        const context = canvas.getContext('2d');
        
        // Create a radial gradient for a glowing particle
        const gradient = context.createRadialGradient(
            canvas.width / 2, canvas.height / 2, 0,
            canvas.width / 2, canvas.height / 2, canvas.width / 2
        );
        
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
        gradient.addColorStop(0.2, 'rgba(160, 220, 255, 0.8)');
        gradient.addColorStop(0.5, 'rgba(100, 180, 255, 0.4)');
        gradient.addColorStop(1, 'rgba(30, 60, 255, 0)');
        
        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        const texture = new THREE.CanvasTexture(canvas);
        return texture;
    }
    
    /**
     * Animate the energy line and particles
     */
    animateEnergyLine() {
        if (!this.verticalDivider) return;
        
        // Create animation function
        const animate = () => {
            if (!this.verticalDivider) return;
            
            const time = Date.now() * 0.001;
            
            // Pulse the main line
            if (this.energyLine && this.energyLine.material) {
                this.energyLine.material.opacity = 0.5 + Math.sin(time * 2) * 0.3;
            }
            
            // Animate particles
            if (this.particles && this.particlePositions) {
                const particleCount = this.particlePositions.length / 3;
                const lineLength = this.linePoints.length;
                
                for (let i = 0; i < particleCount; i++) {
                    // Move particles along the line
                    const speedFactor = this.particleSpeedFactors[i];
                    const particlePosition = (time * speedFactor) % 1;
                    
                    // Map position to a point on the line
                    const pointIndex = Math.floor(particlePosition * (lineLength - 1));
                    const point = this.linePoints[pointIndex];
                    
                    // Update particle position
                    this.particlePositions[i * 3] = point.x + (Math.random() - 0.5) * 0.02; // Add slight randomness
                    this.particlePositions[i * 3 + 1] = point.y;
                    this.particlePositions[i * 3 + 2] = point.z + 0.02;
                    
                    // Pulse particle size
                    this.particleSizes[i] = (0.02 + Math.sin(time * 3 + i) * 0.01) * speedFactor;
                    
                    // Pulse particle opacity
                    this.particleOpacities[i] = 0.5 + Math.sin(time * 2 + i * 0.5) * 0.3;
                }
                
                // Update geometry attributes
                this.particles.geometry.attributes.position.needsUpdate = true;
                this.particles.geometry.attributes.size.needsUpdate = true;
                this.particles.geometry.attributes.opacity.needsUpdate = true;
            }
            
            // Continue animation
            this.energyLineAnimationId = requestAnimationFrame(animate);
        };
        
        // Start animation
        animate();
    }
    
    /**
     * Remove the vertical divider and clean up animations
     */
    removeVerticalDivider() {
        // Stop the animation
        if (this.energyLineAnimationId) {
            cancelAnimationFrame(this.energyLineAnimationId);
            this.energyLineAnimationId = null;
        }
        
        // Clean up energy line
        if (this.energyLine) {
            if (this.energyLine.geometry) this.energyLine.geometry.dispose();
            if (this.energyLine.material) this.energyLine.material.dispose();
            this.energyLine = null;
        }
        
        // Clean up particles
        if (this.particles) {
            if (this.particles.geometry) this.particles.geometry.dispose();
            if (this.particles.material) {
                if (this.particles.material.map) this.particles.material.map.dispose();
                this.particles.material.dispose();
            }
            this.particles = null;
        }
        
        // Clear references
        this.particlePositions = null;
        this.particleSizes = null;
        this.particleOpacities = null;
        this.particleSpeedFactors = null;
        this.linePoints = null;
        
        // Remove the divider group
        if (this.verticalDivider) {
            this.remove(this.verticalDivider);
            this.verticalDivider = null;
        }
    }
    
    /**
     * Add OB/AI hemisphere labels to the pod with 3D depth
     */
    addHemisphereLabels() {
        // Import the TextGeometry if not already available
        if (!THREE.FontLoader || !THREE.TextGeometry) {
            console.warn('TextGeometry not available, falling back to plane geometry');
            this.addFallbackHemisphereLabels();
            return;
        }
        
        // Load a font
        const fontLoader = new THREE.FontLoader();
        
        // Use a callback approach to handle font loading
        const createTextMesh = (text, color, position, isLeft) => {
            // Create a temporary placeholder
            const tempGeometry = new THREE.PlaneGeometry(0.4, 0.2);
            const tempMaterial = new THREE.MeshBasicMaterial({ 
                color: isLeft ? 0x004D40 : 0xC8A951,
                transparent: true,
                opacity: 0.5
            });
            
            const tempMesh = new THREE.Mesh(tempGeometry, tempMaterial);
            tempMesh.position.copy(position);
            this.add(tempMesh);
            
            // Use a local font or a web font
            fontLoader.load('https://threejs.org/examples/fonts/helvetiker_bold.typeface.json', (font) => {
                // Create 3D text geometry
                const textGeometry = new THREE.TextGeometry(text, {
                    font: font,
                    size: 0.15,
                    height: 0.03, // Depth of the text
                    curveSegments: 12,
                    bevelEnabled: true,
                    bevelThickness: 0.01,
                    bevelSize: 0.005,
                    bevelOffset: 0,
                    bevelSegments: 5
                });
                
                // Center the text geometry
                textGeometry.computeBoundingBox();
                const textWidth = textGeometry.boundingBox.max.x - textGeometry.boundingBox.min.x;
                textGeometry.translate(-textWidth / 2, -0.05, 0);
                
                // Create materials for the text
                const mainMaterial = new THREE.MeshStandardMaterial({
                    color: isLeft ? 0x004D40 : 0xC8A951,
                    metalness: 0.8,
                    roughness: 0.2,
                    emissive: isLeft ? 0x004D40 : 0xC8A951,
                    emissiveIntensity: 0.6
                });
                
                const sideMaterial = new THREE.MeshStandardMaterial({
                    color: isLeft ? 0x002A20 : 0x9A7F3D, // Darker shade for sides
                    metalness: 0.7,
                    roughness: 0.3
                });
                
                // Create the 3D text mesh with different materials for front and sides
                const textMesh = new THREE.Mesh(textGeometry, [mainMaterial, sideMaterial]);
                textMesh.position.copy(position);
                
                // Add a subtle animation
                const originalY = position.y;
                const originalZ = position.z;
                
                const animate = () => {
                    const time = Date.now() * 0.001;
                    textMesh.position.y = originalY + Math.sin(time) * 0.01;
                    textMesh.position.z = originalZ + Math.sin(time * 0.5) * 0.005;
                    textMesh.animationId = requestAnimationFrame(animate);
                };
                
                // Start the animation
                animate();
                
                // Replace the placeholder with the actual text
                this.remove(tempMesh);
                tempMesh.geometry.dispose();
                tempMesh.material.dispose();
                
                // Store the reference to the label
                if (isLeft) {
                    this.leftLabel = textMesh;
                } else {
                    this.rightLabel = textMesh;
                }
                
                this.add(textMesh);
            });
        };
        
        // Create the 3D text meshes
        createTextMesh('OB', '#004D40', new THREE.Vector3(-0.3, -0.5, 1.01), true);
        createTextMesh('AI', '#C8A951', new THREE.Vector3(0.3, -0.5, 1.01), false);
    }
    
    /**
     * Fallback method for hemisphere labels if 3D text is not available
     */
    addFallbackHemisphereLabels() {
        // Create canvas elements to render text with glow effect
        const createTextTexture = (text, color) => {
            const canvas = document.createElement('canvas');
            canvas.width = 256; // Increased resolution
            canvas.height = 128; // Increased resolution
            const context = canvas.getContext('2d');
            
            // Add glow effect
            context.shadowColor = color;
            context.shadowBlur = 15;
            context.shadowOffsetX = 0;
            context.shadowOffsetY = 0;
            
            // Set text properties
            context.fillStyle = color;
            context.font = 'bold 72px Arial';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            
            // Draw text with glow
            context.fillText(text, 128, 64);
            
            // Create texture from canvas
            const texture = new THREE.CanvasTexture(canvas);
            texture.minFilter = THREE.LinearFilter; // Better quality when scaled
            texture.magFilter = THREE.LinearFilter;
            return texture;
        };
        
        // Create OB label (left side) with deeper green color
        const obTexture = createTextTexture('OB', '#004D40'); // Deep emerald green
        const obMaterial = new THREE.MeshStandardMaterial({ 
            map: obTexture, 
            transparent: true,
            side: THREE.DoubleSide,
            emissive: new THREE.Color(0x004D40), // Add emissive for glow effect
            emissiveIntensity: 1.2, // Control glow intensity
            emissiveMap: obTexture // Use the same texture for the glow
        });
        const obGeometry = new THREE.PlaneGeometry(0.4, 0.2);
        this.leftLabel = new THREE.Mesh(obGeometry, obMaterial);
        this.leftLabel.position.set(-0.3, -0.5, 1.01); // Below left eye
        this.add(this.leftLabel);
        
        // Create AI label (right side)
        const aiTexture = createTextTexture('AI', '#C8A951'); // Updated gold color
        const aiMaterial = new THREE.MeshStandardMaterial({ 
            map: aiTexture, 
            transparent: true,
            side: THREE.DoubleSide,
            emissive: new THREE.Color(0xC8A951), // Add emissive for glow effect
            emissiveIntensity: 1.0, // Control glow intensity
            emissiveMap: aiTexture // Use the same texture for the glow
        });
        const aiGeometry = new THREE.PlaneGeometry(0.4, 0.2);
        this.rightLabel = new THREE.Mesh(aiGeometry, aiMaterial);
        this.rightLabel.position.set(0.3, -0.5, 1.01); // Below right eye
        this.add(this.rightLabel);
    }
    
    /**
     * Remove hemisphere labels
     */
    removeHemisphereLabels() {
        // Properly dispose of left label
        if (this.leftLabel) {
            this.remove(this.leftLabel);
            
            // Dispose of geometry
            if (this.leftLabel.geometry) {
                this.leftLabel.geometry.dispose();
            }
            
            // Dispose of materials (could be an array for 3D text)
            if (Array.isArray(this.leftLabel.material)) {
                this.leftLabel.material.forEach(material => {
                    if (material) material.dispose();
                });
            } else if (this.leftLabel.material) {
                this.leftLabel.material.dispose();
            }
            
            // Cancel any animations
            if (this.leftLabel.animationId) {
                cancelAnimationFrame(this.leftLabel.animationId);
            }
            
            this.leftLabel = null;
        }
        
        // Properly dispose of right label
        if (this.rightLabel) {
            this.remove(this.rightLabel);
            
            // Dispose of geometry
            if (this.rightLabel.geometry) {
                this.rightLabel.geometry.dispose();
            }
            
            // Dispose of materials (could be an array for 3D text)
            if (Array.isArray(this.rightLabel.material)) {
                this.rightLabel.material.forEach(material => {
                    if (material) material.dispose();
                });
            } else if (this.rightLabel.material) {
                this.rightLabel.material.dispose();
            }
            
            // Cancel any animations
            if (this.rightLabel.animationId) {
                cancelAnimationFrame(this.rightLabel.animationId);
            }
            
            this.rightLabel = null;
        }
    }
}
